/* Core & libraries */
import React, {useEffect, useState} from "react";
import {FormattedMessage} from 'react-intl';

/* Helpers, constants, custom methods, internal libraries */
import {useGenericApiFetch} from "../endpoints/base";
import {
  LOYALTY_TERMSACCEPTED_ENDPOINT,
  LOYALTY_ALLOTMENTS_ENDPOINT,
  LOYALTY_TIERS_ENDPOINT,
  LOYALTY_TIERS_CHECK_ENDPOINT,
  LOYALTY_REDEEM_ENDPOINT,
  LOYALTY_TERMS_AND_CONDITIONS_ENDPOINT,
  LOYALTY_SETTINGS_ENDPOINT,
  LOYALTY_DELETE_REDEMPTIONS,
  LOYALTY_ATM_TEMPLATE_DOWNLOAD_ENDPOINT,
  ALLOTMENT_BUDGET_YEAR_ENDPOINT,
  GET_NOTIFICATION_BANNER_DETAILS_ENDPOINT,
  SET_NOTIFICATION_BANNER_DATA_ENDPOINT,
  GET_ADMIN_LOYALTY_TERMS_CONTENT_ENDPOINT
} from "../endpoints/endpoints";
import {useUserContext} from "../contexts/userContext";
import {useNotificationContext} from "../contexts/notificationContext";
import csvDownload from "json-to-csv-export";
import { useLocaleContext } from '../contexts/localeContext';

/* Components */
/* Styles */

export const useLoyaltyService = ({getRedemptionHistoryCount} = {}) => {
  const [loyaltyAcceptedResponse, setLoyaltyAcceptedResponse] = useState({});
  const [showLoyaltyBannerResponse, setShowLoyaltyBannerResponse] = useState(false);
  const [loyaltyResponse, setLoyaltyResponse] = useState({});
  const [loyaltyTiersResponse, setLoyaltyTiersResponse] = useState({});

  const {setNotification} = useNotificationContext();
  const {genericApiFetch} = useGenericApiFetch();
  const userDetails = useUserContext();
  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;
  const locale = useLocaleContext();

  const getLoyaltyTermsAccepted = async (aaAdminCompanyEntityId) => {
    const response = await genericApiFetch({
      url: LOYALTY_TERMSACCEPTED_ENDPOINT.replace("{companyEntityId}", aaAdminCompanyEntityId ? aaAdminCompanyEntityId : companyEntityId),
      method: "GET",
    });

    if (response.success) {
      setLoyaltyAcceptedResponse(response.data.lepTermsAccepted);
      setShowLoyaltyBannerResponse(response.data.showLoyaltyBanner);
    } else {
      setLoyaltyAcceptedResponse(false);
      setShowLoyaltyBannerResponse(false); 
    }
  };

  const getLoyaltyList = async (options = {}) => {
    const response = await genericApiFetch({
      url: LOYALTY_ALLOTMENTS_ENDPOINT.replace("{companyEntityId}", companyEntityId),
      method: "GET",
      queryParams: options,
      spinner: "loyaltyGridSpinner"
    });

    if (response.success) {
      setLoyaltyResponse(response.data);
    }
  };

  const getTiersList = async () => {
    const response = await genericApiFetch({
      url: LOYALTY_TIERS_ENDPOINT,
      method: "GET",
    });
    if (response.success) {
      setLoyaltyTiersResponse(response.data);
    } else {
      setNotification(
        "error",
        <FormattedMessage id="loyaltyAssignIndividually.errorTitle"/>,
        <FormattedMessage id="loyaltyAssignIndividually.errorText"/>
      );
    }
    return false;
  };

  const getUserTiersCheck = async (entity) => {
    const response = await genericApiFetch({
      url: LOYALTY_TIERS_CHECK_ENDPOINT.replace("{userEntityId}", entity),
      method: "GET",
    });
    if (response.success) {
      return response.data;
    }
    setNotification(
      "error",
      <FormattedMessage id="loyaltyAssignIndividually.errorTitle"/>,
      <FormattedMessage id="loyaltyAssignIndividually.errorText"/>
    );
    return false;
  };

  const addRedemption = async (allotmentId, payload) => {
    const response = await genericApiFetch({
      url: LOYALTY_REDEEM_ENDPOINT.replace("{allotmentId}", allotmentId),
      method: "POST",
      data: payload,
      spinner: "addRedemptionSpinner",
    });
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="loyaltyAssignIndividually.successTitle"/>,
        <FormattedMessage id="loyaltyAssignIndividually.successText"/>
      );
    } else {
      setNotification(
        "error",
        <FormattedMessage id="loyaltyAssignIndividually.errorTitle"/>,
        <FormattedMessage id="loyaltyAssignIndividually.errorText"/>
      );
    }

    if (typeof getRedemptionHistoryCount === 'function') getRedemptionHistoryCount();

    return response.success;
  };

  const removeFailedRedemptions = async (payload) => {
    const response = await genericApiFetch({
      url: LOYALTY_DELETE_REDEMPTIONS.replace("{companyEntityId}", companyEntityId),
      method: "POST",
      data: payload,
      spinner: "deleteRedemptionSpinner"
    })

    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="deleteRedemption.notificationTitle"/>,
        <FormattedMessage id="deleteRedemption.notificationMessage"/>
      )
      if (typeof getRedemptionHistoryCount === 'function') getRedemptionHistoryCount();
      return true;
    }
    setNotification(
      "error",
      <FormattedMessage id="deleteRedemption.error.notificationTitle"/>,
      <FormattedMessage id="deleteRedemption.error.notificationMessage"/>
    )
    return false
  }

  const loyaltyAtmDownloadSample = async () => {

    const response = await  genericApiFetch({
      url: LOYALTY_ATM_TEMPLATE_DOWNLOAD_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyEntityId}", companyEntityId),
      method: "GET",
      spinner: "loyaltyAtmDownloadSpinner",
    })
    if (response.success) {
        const { headers, users } = response.data.response;
       //Add headers to the CSV
        const csvRows = [headers.join(",")];

        // Add each user's data to the CSV
        users.forEach((user) => {
          const row = headers.map((header) => user[header]);
          csvRows.push(row.join(","));
        });

      return csvRows.join("\n")
    } else {
      setNotification(
        "error",
        <FormattedMessage id="loyalty.atmBulkImport.reportErrorTitle" />,
        <FormattedMessage id="loyalty.atmBulkImport.reportErrorText" />
      )
      return false
    }
  };

  const getNotificationBanner = async () => {
    const response = await genericApiFetch({
      url: GET_NOTIFICATION_BANNER_DETAILS_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{language}", locale),
      method: "GET"
    });
    if (response.success) {
      return response.data;
    }
    return false;
  };

  const setNotificationBanner = async (reqBody) => {
    const response = await genericApiFetch({
      url: SET_NOTIFICATION_BANNER_DATA_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()),
      method: "POST",
      data: reqBody
    })

    if (response.success) {
      return true;
    }

    setNotification(
      "error",
      <FormattedMessage id="notifications.revokeErrorTitle" />,
      <FormattedMessage id="notifications.revokeErrorText" />
    )
    return false
  }

  const getTermsContent = async (locale) => {
    const response = await genericApiFetch({
      url: GET_ADMIN_LOYALTY_TERMS_CONTENT_ENDPOINT.replace("{language}", locale.label),
      method: "GET",
      spinner: "termsContentSpinner"
    });
    if (response.success) {
      return response.data;
    }

    return false;
  };

  return {
    loyaltyAcceptedResponse,
    loyaltyResponse,
    loyaltyTiersResponse,
    getLoyaltyTermsAccepted,
    getLoyaltyList,
    getTiersList,
    getUserTiersCheck,
    addRedemption,
    removeFailedRedemptions,
    loyaltyAtmDownloadSample,
    getNotificationBanner,
    setNotificationBanner,
    showLoyaltyBannerResponse,
    getTermsContent
  };
}

export function useLoyaltyTermsAndConditionsService() {
  const [showLoyaltyConsent, setShowLoyaltyConsent] = useState(false);

  const {genericApiFetch} = useGenericApiFetch();
  const userDetails = useUserContext();
  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId

  const postTermsAndConditions = async (payload) => {
    const response = await genericApiFetch({
      url: LOYALTY_TERMS_AND_CONDITIONS_ENDPOINT.replace("{companyEntityId}", companyEntityId),
      method: "POST",
      data: payload
    });

    if (response.success) {
      setShowLoyaltyConsent(false);
    }
  }

  useEffect(() => {
    const init = async () => {
      const response = await genericApiFetch({
        url: LOYALTY_SETTINGS_ENDPOINT,
        method: "GET",
      });

      if (response.success) {
        setShowLoyaltyConsent(!(response.data.termsUpdatedBy && response.data.termsUpdatedOn))
      }
    }

    init();
  }, []);

  return {
    showLoyaltyConsent,
    postTermsAndConditions,
  }
}

export function useAllotmentBudgetYear() {
  const [allotmentBudgetYear, setAllotmentBudgetYear] = useState([]);

  const {genericApiFetch} = useGenericApiFetch();

  const getAllotmentBudgetYear = async () => {
    const response = await genericApiFetch({
      url: ALLOTMENT_BUDGET_YEAR_ENDPOINT,
      method: "GET",
    });

    if (response.success) {
      setAllotmentBudgetYear(response.data.budgetYears?.map(option => ({
        label: `${option.allotmentTypeName} ${option.budgetYear}`,
        value: `${option.loyaltyAllotmentTypeId}-${option.budgetYear}`
      })));
    }
  }

  return {
    allotmentBudgetYear,
    getAllotmentBudgetYear
  }
}